import React, { useState } from 'react';
import Button from '@atlaskit/button';
import LoadingButton from '@atlaskit/button/loading-button';
import { ErrorMessage, Field } from '@atlaskit/form';
import EditorSuccessIcon from '@atlaskit/icon/glyph/editor/success';
import Textfield from '@atlaskit/textfield';
import { G500 } from '@atlaskit/theme/colors';
import { renderPromotionOption } from '@css-xp/base';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import { EntitlementDetails, PromoCodeDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { validateApplicablePromocode } from './queries';
import './change-subscription-pane.scss';

interface Props {
  index: number;
  formValue: string;
  entitlement: EntitlementDetails;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>, i: number) => void;
  handleRemove: (i: number) => void;
  validPromoCodeData: Partial<PromoCodeDetails[]>;
  setValidPromoCodeData: (data: Partial<PromoCodeDetails[]>) => void;
  activePromoCodeData: Partial<PromoCodeDetails[]>;
  setActivePromoCodeData: (data: Partial<PromoCodeDetails[]>) => void;
  isDesignIteration1Enabled?: boolean;
}

export const PROMO_CODE_KEY_PREFIX = 'promoCode-';

export const ManagePromotionField = ({
  index,
  formValue,
  entitlement,
  handleChange,
  handleRemove,
  validPromoCodeData,
  setValidPromoCodeData,
  activePromoCodeData,
  setActivePromoCodeData,
  isDesignIteration1Enabled,
}: Props) => {
  const initialState = {
    isApplyButtonLoading: false,
    promoCodeNotApplicableErrors: null,
    promoCodeAPIError: '',
    validPromotionData: {},
  };
  const [isApplyButtonLoading, setIsApplyButtonLoading] = useState(initialState.isApplyButtonLoading);
  const [promoCodeNotApplicableErrors, setPromoCodeNotApplicableErrors] = useState(
    initialState.promoCodeNotApplicableErrors
  );
  const [promoCodeAPIError, setPromoCodeAPIError] = useState(initialState.promoCodeAPIError);
  const initialPromotionData =
    (activePromoCodeData && activePromoCodeData[index]) || (validPromoCodeData && validPromoCodeData[index]);
  const [validPromotionData, setValidPromotionData] = useState(initialPromotionData ?? initialState.validPromotionData);

  const removeValidPromoStateInSidebar = () => {
    setValidPromoCodeData([]);
  };

  const addValidPromoStateInSidebar = (promoData: PromoCodeDetails) => {
    const newData = [promoData];

    setValidPromoCodeData(newData);
  };

  const handleApplyPromoCode = async () => {
    setIsApplyButtonLoading(true);
    const { data, error } = await graphqlClient().query({
      query: validateApplicablePromocode,
      variables: {
        id: entitlement?.id,
        promoCodes: [
          {
            code: formValue,
            productKey: entitlement?.productId,
          },
        ],
        transactionAccountId: entitlement?.transactionAccountId,
      },
      fetchPolicy: 'no-cache',
    });

    setIsApplyButtonLoading(false);

    if (error) {
      setPromoCodeAPIError(error?.toString());
      setValidPromotionData(initialState.validPromotionData);
      removeValidPromoStateInSidebar();
    } else if (data?.entitlementDetails?.promoCodesApplicable?.notApplicablePromotionCodesWithReason) {
      setPromoCodeNotApplicableErrors(
        data?.entitlementDetails?.promoCodesApplicable?.notApplicablePromotionCodesWithReason?.join('.')
      );
      setValidPromotionData(initialState.validPromotionData);
      removeValidPromoStateInSidebar();
    } else if (data?.entitlementDetails?.promoCodesApplicable?.promotionId) {
      const promoData = data?.entitlementDetails?.promoCodesApplicable?.promotions[0];

      setValidPromotionData(promoData);
      addValidPromoStateInSidebar(promoData);
    }
  };

  const resetToInitialState = () => {
    setIsApplyButtonLoading(initialState.isApplyButtonLoading);
    setPromoCodeNotApplicableErrors(initialState.promoCodeNotApplicableErrors);
    setPromoCodeAPIError(initialState.promoCodeAPIError);
    setValidPromotionData(initialState.validPromotionData);
  };

  const renderRemoveButton = () => {
    return (
      <Button
        appearance="link"
        spacing="none"
        testId={'remove-promo-code-' + index}
        onClick={() => {
          resetToInitialState();
          removeValidPromoStateInSidebar();
          handleRemove(index);
          setActivePromoCodeData([]);
        }}
        title="Remove the active promo code first before adding a new one"
      >
        Remove
      </Button>
    );
  };

  const isPromoCodeValid: boolean = Object.keys(validPromotionData)?.length > 0;
  const shouldHideInput: boolean = activePromoCodeData?.length > 0;

  return (
    // Adding `defaultValue` in Field is important otherwise the formState is not updated while moving
    // to the next section and returning to the promotion section (component re-mount).
    <Field name={PROMO_CODE_KEY_PREFIX + index} defaultValue={formValue}>
      {({ fieldProps }) => (
        <>
          {shouldHideInput || (
            <div className="promo-code-container">
              <Textfield
                {...fieldProps}
                name={PROMO_CODE_KEY_PREFIX + index}
                testId={'promo-code-' + index}
                placeholder={isDesignIteration1Enabled ? undefined : 'Enter vendor promotion'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;

                  fieldProps.onChange(value);
                  handleChange(e, index);
                }}
                isDisabled={isApplyButtonLoading || isPromoCodeValid}
                elemAfterInput={isPromoCodeValid && <EditorSuccessIcon size="large" primaryColor={G500} label="" />}
              />
              {isPromoCodeValid ? (
                <>
                  <Button
                    appearance="link"
                    spacing="none"
                    testId={'edit-promo-code-' + index}
                    onClick={() => {
                      resetToInitialState();
                      removeValidPromoStateInSidebar();
                    }}
                  >
                    Edit
                  </Button>
                  {renderRemoveButton()}
                </>
              ) : (
                <LoadingButton
                  appearance="primary"
                  isSelected
                  isLoading={isApplyButtonLoading}
                  isDisabled={isApplyButtonLoading || !formValue}
                  testId={'apply-promo-code-' + index}
                  aria-label="Apply promo code"
                  onClick={() => {
                    resetToInitialState();
                    handleApplyPromoCode();
                  }}
                >
                  Apply
                </LoadingButton>
              )}
            </div>
          )}
          <div>
            {isPromoCodeValid && (
              <div className="promo-code-promotion-details">
                <div>{renderPromotionOption(validPromotionData, { showBenefitNextLine: true })}</div>
                {shouldHideInput && renderRemoveButton()}
              </div>
            )}
            {promoCodeNotApplicableErrors && <ErrorMessage>{promoCodeNotApplicableErrors}</ErrorMessage>}
            {promoCodeAPIError && <ErrorMessage>{promoCodeAPIError}</ErrorMessage>}
          </div>
        </>
      )}
    </Field>
  );
};
